function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { QueryReportHeaderReact } from 'common/query-builder/query-report-header';
import { EntityType } from 'common/report/util/saved-entities/types';
import { SearchDisplayContext } from 'common/report/util/search/types';
import { HeaderAction, QueryReportHeaderParams } from 'common/query-builder/query-report-header/types';
import { buildQueryReportHeaderParams } from 'common/report/util/saved-entities';
import { ToastType } from 'common/components/toast/types';
import { addToast } from 'common/components/toast-area/toast-manager';
import { getAppUrl } from 'common/report/util';
import { useTranslation } from 'common/i18n/hooks';
import { useFeatureFlagStore } from 'apps/feature-flags/src/store';
import { useFeatureFlagsApi } from 'apps/feature-flags/src/api/feature-flags';
import { userDetailsStore } from 'common/report/stores/user-details';
import { useFeatureFlagPageContext } from 'iron/apps/feature-flags/src/components/context';
import { useCreateFeatureFlag } from 'iron/apps/feature-flags/src/components/hooks/useCreateFeatureFlag';
import { payloadParamsFromModel } from 'iron/apps/feature-flags/src/components/util';
import { FeatureFlagApiResponse } from 'app_api/projects/feature_flags/types';
import { trackFeatureFlagViewPageAction, TrackingEventSource, ViewPageTrackingEvent } from 'apps/feature-flags/src/tracking';
import styles from './index.module.styl';
interface FeatureFlagHeaderProps {
  onUpdateFlag: (updatedFlag: FeatureFlagApiResponse) => void;
}
export function FeatureFlagHeader(_ref: FeatureFlagHeaderProps) {
  let {
    onUpdateFlag
  }: FeatureFlagHeaderProps = _ref;
  const i18n = useTranslation();
  const navigate = useNavigate();
  const {
    projectId,
    userId,
    workspaceId,
    featureFlagEntity
  } = useFeatureFlagPageContext();
  const currentFeatureFlag = useFeatureFlagStore(state => state.currentFeatureFlag);
  const {
    deleteFeatureFlag
  } = useFeatureFlagsApi({
    projectId
  });
  const createFeatureFlag = useCreateFeatureFlag();
  const [forceUpdateState, forceUpdate] = useState(0);
  const trackDeletionEvent = (isSuccess: boolean, featureFlag?: FeatureFlagApiResponse) => {
    trackFeatureFlagViewPageAction({
      event: ViewPageTrackingEvent.DeleteFeatureFlag,
      source: TrackingEventSource.ViewPageHeader,
      featureFlag,
      props: {
        isSuccess
      }
    });
  };
  const deleteMutation = useMutation({
    mutationFn: deleteFeatureFlag,
    onSuccess: () => {
      trackDeletionEvent(true, currentFeatureFlag);
      navigate(getAppUrl("feature-flags"));
      addToast({
        message: i18n.t("feature-flags.page.delete.success"),
        type: ToastType.SUCCESS,
        timeout: 6000
      });
    },
    onError: () => {
      trackDeletionEvent(false);
      addToast({
        message: i18n.t("feature-flags.page.delete.error"),
        type: ToastType.ERROR,
        timeout: 6000
      });
    }
  });
  useEffect(() => {
    const userDetailsSubscriber = () => forceUpdate(forceUpdateState + 1);
    userDetailsStore.subscribe(userDetailsSubscriber);
    return () => {
      userDetailsStore.unsubscribe(userDetailsSubscriber);
    };
  }, []);
  const queryReportHeaderParams: QueryReportHeaderParams = useMemo(() => {
    return _objectSpread(_objectSpread({}, buildQueryReportHeaderParams({
      projectId,
      entityType: EntityType.FeatureFlag,
      entity: featureFlagEntity!,
      invalidEntity: !(currentFeatureFlag !== null && currentFeatureFlag !== void 0 && currentFeatureFlag.id),
      displayContext: SearchDisplayContext.FeatureFlag
    })), {}, {
      displayContext: SearchDisplayContext.FeatureFlag,
      entityType: EntityType.FeatureFlag,
      trackName: "[FeatureFlag]",
      overflowControlItems: [],
      hasUnsavedChanges: false,
      invalidMessage: "",
      projectId,
      userId,
      workspaceId
    });
  }, [userDetailsStore.value, featureFlagEntity, currentFeatureFlag]);
  const handleHeaderActionClick = (ev: CustomEvent) => {
    const actionName = ev.detail.name || ev.detail.value;
    if (actionName === HeaderAction.Delete) {
      deleteMutation.mutate({
        id: currentFeatureFlag!.id
      });
    } else if (actionName === HeaderAction.Duplicate) {
      const newFeatureFlag = payloadParamsFromModel(currentFeatureFlag!);
      createFeatureFlag(_objectSpread(_objectSpread({}, newFeatureFlag), {}, {
        name: "Copy of ".concat(currentFeatureFlag!.name),
        key: "copy_of_".concat(currentFeatureFlag!.key)
      }));
    }
  };
  const handleSave = (ev: CustomEvent<{
    name: string;
  }>) => {
    if (currentFeatureFlag) {
      const updatedFlag: FeatureFlagApiResponse = _objectSpread(_objectSpread({}, currentFeatureFlag), {}, {
        name: ev.detail.name
      });
      onUpdateFlag(updatedFlag);
    }
  };
  return <div className={styles.container} data-sentry-component="FeatureFlagHeader" data-sentry-source-file="index.tsx">
      <QueryReportHeaderReact {...queryReportHeaderParams} onActionclick={handleHeaderActionClick} onSave={handleSave} data-sentry-element="QueryReportHeaderReact" data-sentry-source-file="index.tsx" />
    </div>;
}